import {
  MUTATION_RESPONSE_RECEIVED,
  QUERY_RESPONSE_RECEIVED,
} from '@web-platform/shared-universal-constants/src/network';
import {
  PAYMENT_DISCOUNT_MAX_RETRIES_REACHED,
  PAYMENT_DISCOUNT_TOOLTIP_DISMISSED,
  SET_PAYMENT_METHOD_FILTER,
} from '@web-platform/shared-universal-constants/src/redux/actions/paymentDiscounts';
import {
  PAYMENT_DISCOUNT,
  SONAR_V3_ALPHA_CREATE_SESSION,
  SONAR_V3_CREATE_SESSION,
  SONAR_V3_POLL,
  SONAR_V3_POLL_ALPHA,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { PricingOptionStatus } from '@web-platform/shared-universal-types/src/network/types';
import { formatMoney } from '@web-platform/shared-universal-utils-frontend/src/helpers/formatMoney';
import { isSonarV3ErrorPayload } from '@web-platform/shared-universal-utils-frontend/src/helpers/isSonarErrorPayload';

import { isEmptyItinerary, isNullishItinerary, isSonarV3ItineraryNotFound } from '../flights/utils';

import type {
  MutationAction,
  MutationResponseAction,
  QueryAction,
  QueryResponseAction,
} from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type {
  DismissPaymentDiscountTooltipAction,
  PaymentDiscountMaxRetriesReachedAction,
  SetPaymentMethodFilterAction,
} from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type {
  PaymentDiscountsItinerariesBookingPanel,
  PaymentDiscountsStateBookingPanel,
} from '@web-platform/shared-universal-types/src/redux/state/shared/flightBookingPanel/entities/paymentDiscountsBookingPanel';

export * from './selectors';

export const INITIAL_STATE: PaymentDiscountsStateBookingPanel = {
  itineraries: { byId: {} },
  selectedPaymentMethodFilter: undefined,
};

type PaymentDiscountsAction =
  | MutationAction
  | QueryAction
  | PaymentDiscountMaxRetriesReachedAction
  | SetPaymentMethodFilterAction
  | DismissPaymentDiscountTooltipAction;

const byId = (
  state: PaymentDiscountsItinerariesBookingPanel,
  action: QueryResponseAction | MutationResponseAction,
) => {
  switch (action.payload.type) {
    case PAYMENT_DISCOUNT: {
      const { response } = action.payload;

      if (!response.headers || !response.body?.itineraryId || !response.body?.pricingOptions) {
        return state;
      }

      const { itineraryId, pricingOptions } = response.body;

      const oldPricingOptions = (state[itineraryId]?.pricingOptions || []).filter(
        (existingOption) =>
          !pricingOptions.some((newOption) => newOption.pricingOptionId === existingOption.id),
      );

      const transformPricingOptions = pricingOptions.map((pricingOption) => ({
        id: pricingOption.pricingOptionId || '',
        status: PricingOptionStatus.CURRENT,
        paymentDiscounts: (pricingOption.paymentDiscounts ?? []).map((discount) => ({
          discountProvider: discount.discountProvider ?? '',
          discountAmount: discount.discountAmount ? formatMoney(discount.discountAmount, false) : 0,
          finalPrice: discount.finalPrice ? formatMoney(discount.finalPrice, false) : 0,
        })),
      }));

      return {
        ...state,
        [itineraryId]: {
          id: itineraryId,
          pricingOptions: [...oldPricingOptions, ...transformPricingOptions],
        },
      };
    }

    case SONAR_V3_ALPHA_CREATE_SESSION:
    case SONAR_V3_CREATE_SESSION:
    case SONAR_V3_POLL:
    case SONAR_V3_POLL_ALPHA: {
      if (
        isSonarV3ItineraryNotFound(action.payload.response) ||
        isSonarV3ErrorPayload(action.payload.response)
      ) {
        return state;
      }
      if (
        isEmptyItinerary(action.payload.response) ||
        isNullishItinerary(action.payload.response)
      ) {
        return state;
      }

      const sonarItinerary = action.payload.response.body.itinerary;
      if (!sonarItinerary?.id || !sonarItinerary?.pricingOptions) {
        return state;
      }

      const stateItinerary = state[sonarItinerary.id];

      if (!stateItinerary) {
        return state;
      }

      const updatedPricingOptions = stateItinerary.pricingOptions.map((pricingOption) => {
        if (sonarItinerary.pricingOptions?.some((po) => po.id === pricingOption.id)) {
          return { ...pricingOption, status: PricingOptionStatus.EXPIRED };
        }
        return pricingOption;
      });

      return {
        ...state,
        [stateItinerary.id]: {
          ...stateItinerary,
          pricingOptions: updatedPricingOptions,
        },
      };
    }

    default:
      return state;
  }
};

const handleMaxRetries = (
  state: PaymentDiscountsItinerariesBookingPanel,
  action: PaymentDiscountMaxRetriesReachedAction,
) => {
  const { pricingOptionIds } = action.payload;
  if (!pricingOptionIds || pricingOptionIds.length === 0) {
    return state;
  }

  const stateItinerary = state[action.payload.id];

  if (!stateItinerary) {
    return state;
  }

  const updatedPricingOptions = state[action.payload.id].pricingOptions.map((pricingOption) => ({
    ...pricingOption,
    status: pricingOptionIds.includes(pricingOption.id)
      ? PricingOptionStatus.UNAVAILABLE
      : pricingOption.status,
  }));

  return {
    ...state,
    [action.payload.id]: {
      id: action.payload.id,
      pricingOptions: updatedPricingOptions,
    },
  };
};

const paymentDiscountsReducer = (
  state: PaymentDiscountsStateBookingPanel = INITIAL_STATE,
  action: PaymentDiscountsAction,
): PaymentDiscountsStateBookingPanel => {
  switch (action.type) {
    case SET_PAYMENT_METHOD_FILTER: {
      return { ...state, selectedPaymentMethodFilter: action.payload.names };
    }
    case PAYMENT_DISCOUNT_MAX_RETRIES_REACHED: {
      return { ...state, itineraries: { byId: handleMaxRetries(state.itineraries.byId, action) } };
    }
    case PAYMENT_DISCOUNT_TOOLTIP_DISMISSED: {
      return { ...state, selectedTooltipDismissed: true };
    }
    case MUTATION_RESPONSE_RECEIVED:
    case QUERY_RESPONSE_RECEIVED: {
      return { ...state, itineraries: { byId: byId(state.itineraries.byId, action) } };
    }
    default:
      return state;
  }
};

export default paymentDiscountsReducer;
